import { useState, useEffect, useCallback } from 'react';
import { useImpersonation } from '@center-technologies/shared-api-clients';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import {
  ChannelItem,
  ItemPermissionsResponse,
  ItemSyndicationInfo,
} from 'types/channel';
import { getChannelItems, getItemPermissions } from '../api/marketplaceApi';

/**
 * Snake-case shape embedded on the list response for syndicated items.
 * Not exported from `types/channel` (that module uses the camelCase
 * `ItemSyndicationInfo` façade), so it is mirrored here.
 */
interface ChannelItemSyndicationSource {
  source_channel_system_name: string;
  source_item_type: string;
  source_item_system_name: string;
  version_mode: 'pinned' | 'latest';
  pinned_version: number | null;
  resolved_version: number | null;
}

export interface ChannelItemWithSyndicationSource extends ChannelItem {
  syndication_source?: ChannelItemSyndicationSource | null;
}

export interface UseItemDetailResult {
  item: ChannelItemWithSyndicationSource | null;
  permissions: ItemPermissionsResponse | null;
  syndicationInfo: ItemSyndicationInfo | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

function toSyndicationInfo(
  item: ChannelItemWithSyndicationSource | null
): ItemSyndicationInfo | null {
  if (!item) return null;
  const source = item.syndication_source;
  if (!item.is_syndicated || !source) {
    return { isSyndicated: false };
  }
  return {
    isSyndicated: true,
    sourceChannelSystemName: source.source_channel_system_name,
    sourceItemSystemName: source.source_item_system_name,
    versionMode: source.version_mode,
    pinnedVersion: source.pinned_version ?? undefined,
    resolvedVersion: source.resolved_version ?? undefined,
  };
}

export function useItemDetail(
  channelId: string,
  itemId: string | null
): UseItemDetailResult {
  const apiClients = useApiClientsOrNull();
  const { impersonationDetails } = useImpersonation();
  const organizationId = impersonationDetails?.organizationId;

  const [item, setItem] = useState<ChannelItemWithSyndicationSource | null>(null);
  const [permissions, setPermissions] = useState<ItemPermissionsResponse | null>(null);
  const [syndicationInfo, setSyndicationInfo] = useState<ItemSyndicationInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetchTrigger, setFetchTrigger] = useState(0);

  const refetch = useCallback(() => {
    setFetchTrigger((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (!apiClients || !organizationId || !channelId || !itemId) {
      setItem(null);
      setPermissions(null);
      setSyndicationInfo(null);
      return;
    }

    let cancelled = false;

    const fetchDetail = async () => {
      setLoading(true);
      setError(null);

      try {
        const [itemsResponse, permissionsResponse] = await Promise.all([
          getChannelItems(apiClients.schemaClient, channelId),
          getItemPermissions(apiClients.schemaClient, channelId, itemId),
        ]);

        if (cancelled) return;

        const matched =
          (itemsResponse.data as ChannelItemWithSyndicationSource[]).find(
            (candidate) => candidate.id === itemId
          ) ?? null;

        setItem(matched);
        setPermissions(permissionsResponse);
        setSyndicationInfo(toSyndicationInfo(matched));
      } catch (err) {
        if (!cancelled) {
          const message =
            err instanceof Error ? err.message : 'Failed to load item details';
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchDetail();

    return () => {
      cancelled = true;
    };
  }, [apiClients, organizationId, channelId, itemId, fetchTrigger]);

  return { item, permissions, syndicationInfo, loading, error, refetch };
}
